import React, { useState } from 'react';
import { MdClose, MdFolder, MdCode } from 'react-icons/md';

const ProjectModal = ({ isOpen, onClose, onCreateProject, loading }) => {
  const [formData, setFormData] = useState({
    projectName: '',
    runtimeLanguage: 'python',
    description: ''
  });

  const runtimeLanguages = [
    { value: 'python', label: 'Python', description: 'Python 3.11 runtime environment' },
    { value: 'nodejs', label: 'Node.js', description: 'JavaScript runtime with Node.js 18.x' },
    { value: 'java', label: 'Java', description: 'Java 17 development environment' },
    { value: 'cpp', label: 'C++', description: 'C++ with GCC 11 compiler' },
    { value: 'html', label: 'Static HTML', description: 'Static HTML/CSS/JavaScript' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.projectName.trim()) {
      return;
    }

    const success = await onCreateProject(
      formData.projectName.trim(),
      formData.runtimeLanguage,
      formData.description.trim()
    );

    if (success) {
      // Reset form and close modal
      setFormData({
        projectName: '',
        runtimeLanguage: 'python',
        description: ''
      });
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdFolder className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create New Project</h2>
              <p className="text-sm text-gray-600">Set up a new coding project</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter project name..."
              required
              disabled={loading}
            />
          </div>

          {/* Runtime Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Runtime Language <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.runtimeLanguage}
              onChange={(e) => handleInputChange('runtimeLanguage', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              {runtimeLanguages.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {runtimeLanguages.find(lang => lang.value === formData.runtimeLanguage)?.description}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Optional project description..."
              disabled={loading}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading || !formData.projectName.trim()}
            >
              <MdCode size={16} />
              <span>{loading ? 'Creating...' : 'Create Project'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;